import clsx from "clsx"
import { useEffect, useRef, useState } from "react"

import useDebounce from "@hooks/useDebounce"

import { faIcon } from "@utils/faIcon"

import { Skeleton } from "@particles/index"

import { Icon, IconButton, Tooltip } from "@atoms"

import { CLEAR_SEARCH_EVENT } from "@constants/events"

import styles from "./SearchInput.module.scss"
import { SearchInputProps } from "./SearchInput.types"

const MIN_SEARCH_LENGTH = 2

export default function SearchInput(props: SearchInputProps) {
    const {
        type = "bar",
        defaultValue,
        onValueChange,
        isLoading = false,
        isDisabled = false,
        _isFocused,
        _isHovered,
    } = props

    const [value, setValue] = useState<string>(defaultValue)
    const debouncedValue = useDebounce<string>(value, 500)

    const inputRef = useRef<HTMLInputElement>(null)

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value)
    }

    const handleClear = () => {
        setValue("")
        if (!isDisabled) {
            inputRef.current?.focus()
        }
    }

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    useEffect(() => {
        if (debouncedValue.length >= MIN_SEARCH_LENGTH) {
            onValueChange(debouncedValue)
        } else {
            onValueChange("")
        }
    }, [debouncedValue])

    useEffect(() => {
        window.addEventListener(CLEAR_SEARCH_EVENT, handleClear)

        return () => {
            window.removeEventListener(CLEAR_SEARCH_EVENT, handleClear)
        }
    }, [])

    if (isLoading) {
        return (
            <Skeleton
                width="100%"
                height={type === "field" ? 36 : 40}
                borderRadius={type === "field" ? 6 : 0}
                enableAnimation={true}
            />
        )
    } else {
        return (
            <div
                className={clsx(styles.base, styles[`${type}Type`], {
                    [styles.typed]: debouncedValue.length > 0,
                    [styles.typing]: debouncedValue.length === 0 && value.length > 0,
                    [styles.disabled]: isDisabled,
                    [styles.focused]: _isFocused,
                    [styles.hovered]: _isHovered,
                })}
                aria-disabled={isDisabled}
            >
                <div className={styles.searchAndInput}>
                    <div className={styles.iconContainer}>
                        <Icon
                            icon={faIcon({ name: "magnifying-glass", style: "regular" })}
                            container="square"
                            size={14}
                            className={styles.icon}
                        />
                    </div>

                    <input
                        value={value}
                        onChange={onInputChange}
                        placeholder="Keyword search"
                        className={styles.input}
                        ref={inputRef}
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                    />
                </div>

                {value && !isDisabled && (
                    <Tooltip body="Clear">
                        <IconButton
                            size={type === "field" ? "md" : "lg"}
                            variant="ghost"
                            className={styles.clearButton}
                            icon={faIcon({ name: "circle-xmark", style: "solid" })}
                            onClick={handleClear}
                            aria-label="Clear input"
                            noDebounce={true}
                        />
                    </Tooltip>
                )}
            </div>
        )
    }
}
